import {
  computed,
  ElementRef,
  Injectable,
  linkedSignal,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { environment } from '@environments/environment';
import {
  CampaignTypes,
  DEFAULT_CONFIG,
  DEFAULT_TIMER,
  DISPLAY_CONDITION,
  OfferType,
  Pages,
} from '@shared/constants';
import { Campaign, Reward, RewardPoints, StepChallenge } from '@shared/factory';
import {
  AbTestData,
  AnyChallenge,
  Configs,
  DisplayCondition,
  Environment,
  Timer,
} from '@shared/models';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  public config: WritableSignal<Configs> = signal(DEFAULT_CONFIG);
  public environment: WritableSignal<Environment> = signal(environment);

  // Event Data Ex: Scratch Ticket | Bottom Sheet
  public hasEventData: WritableSignal<{
    bottomSheet: boolean;
    scratchTicket: boolean;
  }> = signal({
    bottomSheet: false,
    scratchTicket: false,
  });
  public currentEventCampaigns = signal<Campaign[]>([]);
  public currentEventData: WritableSignal<AnyChallenge[]> = signal([]);
  public bottomSheetModalToggler: WritableSignal<boolean> = signal(false);

  // Current Loyalty Campaign
  public hasCurrentCampaign: WritableSignal<boolean> = signal(false);
  public currentChallenges: WritableSignal<AnyChallenge[]> = signal([]);
  public currentCampaign: WritableSignal<Campaign> = signal({} as Campaign);
  public favoriteBrandsLogos: Signal<string[]> = computed(() => {
    return this.currentChallenges()
      .filter(
        (item: AnyChallenge): item is StepChallenge =>
          item.type === OfferType.STEP
      )
      .slice(0, 4)
      .map((item) => item.productScope.logoPath);
  });
  public completedChallenges: Signal<number> = computed(() =>
    countCompletedChallenges(this.currentChallenges())
  );
  public selectedCurrentChallenges: Signal<number> = computed(() => {
    const selectedChallenges = this.currentChallenges().filter(
      (challenge: AnyChallenge) =>
        challenge.isSelected &&
        ![OfferType.SELECT_TO_WIN].includes(challenge.type)
    );
    return selectedChallenges.length;
  });

  // Future Loyalty Campaign
  public hasFutureCampaign: WritableSignal<boolean> = signal(false);
  public futureChallenges: WritableSignal<AnyChallenge[]> = signal([]);
  public futureCampaignLoaded: WritableSignal<boolean> = signal(false);
  public futureCampaign: WritableSignal<Campaign> = signal({} as Campaign);
  public futureChallengeshasBeenLoaded: WritableSignal<boolean> = signal(false);
  public selectedFutureChallenges: Signal<number> = computed(() => {
    const selectedChallenges = this.futureChallenges().filter(
      (challenge: AnyChallenge) =>
        challenge.isSelected &&
        ![OfferType.SELECT_TO_WIN].includes(challenge.type)
    );
    return selectedChallenges.length;
  });

  // Completed Loyalty Campaign
  public hasCompletedCampaigns: WritableSignal<boolean> = signal(false);
  public completedCampaigns: WritableSignal<Campaign[]> = signal([]);
  public completedLoyaltyData: WritableSignal<{
    [campaignId: number]: {
      loaded: boolean;
      challenges: AnyChallenge[];
    };
  }> = signal({});

  // Current Bis Campaign
  public hasCurrentBisCampaign: WritableSignal<boolean> = signal(false);
  public currentBisCampaigns: WritableSignal<Campaign[]> = signal([]);
  public currentBisCampaignsByKeys: Signal<{ [key: string]: Campaign }> =
    computed(() => {
      return this.currentBisCampaigns().reduce(
        (acc: { [key: string]: Campaign }, campaign: Campaign) => {
          acc[campaign.campaignId] = campaign;
          return acc;
        },
        {}
      );
    });

  public currentBisChallenges: WritableSignal<AnyChallenge[]> = signal([]);
  // Sort Current Bis Challenges Rules:
  // On the first computation (or when the set of challenges changes):
  //   - Selected challenges first
  //   - Then unselected, sorted by most recent campaign start date
  //   - Out of stock at the end
  // After the initial sort, as long as the set of challenges (offerIds) is the same,
  // the previous ordering is preserved, even if selection state changes, to avoid
  // challenges jumping around.
  private bisChallengesOrder: number[] = [];
  public sortedCurrentBisChallenges: Signal<AnyChallenge[]> = computed(() => {
    const challenges = this.currentBisChallenges();

    if (!challenges.length) {
      this.bisChallengesOrder = [];
      return [];
    }

    const currentIds = new Set(challenges.map((c) => c.offerId));
    const isSameList =
      this.bisChallengesOrder.length > 0 &&
      currentIds.size === this.bisChallengesOrder.length &&
      this.bisChallengesOrder.every((id) => currentIds.has(id));

    if (isSameList) {
      const challengeMap = new Map(challenges.map((c) => [c.offerId, c]));
      return this.bisChallengesOrder.map((id) => challengeMap.get(id)!);
    }

    const campaignsByKeys = this.currentBisCampaignsByKeys();

    const outOfStock = challenges.filter((c) => c.isOutOfStock());
    const inStock = challenges.filter((c) => !c.isOutOfStock());
    const selected = inStock.filter((c) => c.isSelected);
    const unselected = inStock.filter((c) => !c.isSelected);

    unselected.sort((a, b) => {
      const startDateA = campaignsByKeys[a.campaignId].startDate;
      const startDateB = campaignsByKeys[b.campaignId].startDate;
      return new Date(startDateB).getTime() - new Date(startDateA).getTime();
    });

    const sorted = [...selected, ...unselected, ...outOfStock];
    this.bisChallengesOrder = sorted.map((c) => c.offerId);
    return sorted;
  });

  public selectedCurrentBisChallenges: Signal<number> = computed(() => {
    const selectedChallenges = this.currentBisChallenges().filter(
      (challenge: AnyChallenge) => challenge.isSelected
    );
    return selectedChallenges.length;
  });
  public completedBisChallenges: Signal<number> = computed(() =>
    countCompletedChallenges(this.currentBisChallenges())
  );

  // Future Bis Campaign
  public hasFutureBisCampaign: WritableSignal<boolean> = signal(false);
  public futureBisCampaignLoaded: WritableSignal<boolean> = signal(false);
  public futureBisChallenges: WritableSignal<AnyChallenge[]> = signal([]);
  public futureBisCampaign: WritableSignal<Campaign> = signal({} as Campaign);
  public futureBisChallengeshasBeenLoaded: WritableSignal<boolean> =
    signal(false);
  public selectedFutureBisChallenges: Signal<number> = computed(() => {
    const selectedChallenges = this.futureBisChallenges().filter(
      (challenge: AnyChallenge) => challenge.isSelected
    );
    return selectedChallenges.length;
  });

  // Completed Bis Campaign
  public hasCompletedBisCampaigns: WritableSignal<boolean> = signal(false);
  public completedBisCampaigns: WritableSignal<Campaign[]> = signal([]);
  public completedBisCampaignsByKeys: Signal<{ [key: string]: Campaign }> =
    computed(() => {
      return this.completedBisCampaigns().reduce(
        (acc: { [key: string]: Campaign }, campaign: Campaign) => {
          acc[campaign.campaignId] = campaign;
          return acc;
        },
        {}
      );
    });
  public completedBisData: WritableSignal<{
    [campaignId: number]: {
      loaded: boolean;
      challenges: AnyChallenge[];
    };
  }> = signal({});

  // Countdown
  public showCountdown: WritableSignal<boolean> = signal(true);
  public countdown: WritableSignal<Timer> = signal(DEFAULT_TIMER);
  public currentPage: WritableSignal<Pages> = signal(Pages.CURRENT);

  // Wallet
  public walletTab: WritableSignal<CampaignTypes> = signal(
    CampaignTypes.LOYALTYCHALLENGE
  );

  /**
   * Align the wallet with a page tab selection.
   * Retailers without a dedicated bis page show a single wallet aggregating
   * main + bis, so the tab must never narrow it down to the bis campaigns.
   * @param tab the selected tab on the page
   */
  public syncWalletTab(tab: CampaignTypes): void {
    const environment = this.environment();
    if (environment.hasCampaignBis && environment.hasBisPage) {
      this.walletTab.set(tab);
    }
  }

  // specific Display Condition for Cetoine
  public toggleWallet: WritableSignal<boolean> = signal(false);

  // How to Play
  // indicates if the customer has played recently means he has challenges in progress
  public hasPlayedRecently: WritableSignal<boolean> = signal(true);
  public checkTutorial(specificDisplayCondition: DisplayCondition): boolean {
    if (specificDisplayCondition === DISPLAY_CONDITION.ALWAYS) {
      return true;
    } else if (specificDisplayCondition === DISPLAY_CONDITION.NEVER) {
      return false;
    }
    return !this.hasPlayedRecently();
  }

  public abTestData: WritableSignal<AbTestData> = signal({});

  // Hints: specific case for Shane AB Test
  public mostActiveChallenges = computed(() => {
    const challenges = this.currentChallenges();
    if (!challenges.length) {
      return [];
    }
    const result = challenges
      .filter(
        (challenge) =>
          challenge.type === OfferType.STEP &&
          challenge.isSelected &&
          challenge.progress > 0 &&
          !challenge.isCompleted
      )
      .slice(0, 3);

    return result;
  });

  public activeTabs: {
    faq: WritableSignal<CampaignTypes>;
    future: WritableSignal<CampaignTypes>;
    current: WritableSignal<CampaignTypes>;
    completed: WritableSignal<CampaignTypes>;
  } = {
    faq: signal(CampaignTypes.LOYALTYCHALLENGE),
    future: signal(CampaignTypes.LOYALTYCHALLENGE),
    current: signal(CampaignTypes.LOYALTYCHALLENGE),
    completed: signal(CampaignTypes.LOYALTYCHALLENGE),
  };

  // Total Reward
  public totalRewardCampaignIds: Signal<number[]> = computed(() => {
    if (this.walletTab() === CampaignTypes.LOYALTYCHALLENGE_BIS) {
      return this.currentBisCampaigns().map((c: Campaign) => c.campaignId);
    } else {
      return this.environment().hasCampaignBis && !this.environment().hasBisPage
        ? [
            this.currentCampaign().campaignId,
            this.currentBisCampaigns().map((c: Campaign) => c.campaignId),
          ].flat()
        : [this.currentCampaign().campaignId];
    }
  });

  // define the signal for the element clicked to show the modal
  public objectRef: WritableSignal<{
    type: string; // Modal type (select, unselect, scanner)
    element: ElementRef; // element clicked
  } | null> = signal(null);

  // Burn Page
  public reloadSlider: WritableSignal<number> = signal(0);
  public rewardPageActiveTab: WritableSignal<number> = signal(0);

  public rewards: WritableSignal<Reward[]> = signal([]);
  public isRewardsLoading: WritableSignal<boolean> = signal(false);
  public isRewardsLoadingError: WritableSignal<boolean> = signal(false);
  public isRewardsLoadingSuccess: WritableSignal<boolean> = signal(false);
  public isRewardsLoadingCompleted: WritableSignal<boolean> = signal(false);
  public rewardsByDisplayOrder = computed((): Reward[][] => {
    const rewards = this.rewards();

    if (!rewards.length) {
      return [];
    }

    const maxRewardBankId = Math.max(...rewards.map((r) => r.rewardBankId));

    // keep only rewards with that max value
    const filtered = rewards.filter((r) => r.rewardBankId === maxRewardBankId);

    const orderedMap = new Map<number, Reward[]>();
    const nullDisplayOrder: Reward[] = [];

    // Split rewards
    for (const r of filtered) {
      if (
        r.categoryDisplayOrder === null ||
        r.categoryDisplayOrder === undefined
      ) {
        nullDisplayOrder.push(r);
      } else {
        const arr = orderedMap.get(r.categoryDisplayOrder);
        if (arr) arr.push(r);
        else orderedMap.set(r.categoryDisplayOrder, [r]);
      }
    }

    // Sort each non-null displayOrder group by priceToUnlock
    for (const arr of orderedMap.values()) {
      arr.sort((a, b) => a.priceToUnlock - b.priceToUnlock);
    }

    // Sort null displayOrder rewards by priceToUnlock
    nullDisplayOrder.sort((a, b) => a.priceToUnlock - b.priceToUnlock);

    // Build final result (non-null displayOrder first, asc)
    const result: Reward[][] = [];

    const sortedKeys = [...orderedMap.keys()].sort((a, b) => a - b);
    for (const key of sortedKeys) {
      result.push(orderedMap.get(key)!);
    }

    // Append null displayOrder rewards last (if any)
    if (nullDisplayOrder.length > 0) {
      result.push(nullDisplayOrder);
    }

    return result;
  });

  public purchasedRewards: WritableSignal<Reward[]> = signal([]);
  public isPurchasedRewardsLoading: WritableSignal<boolean> = signal(false);
  public isPurchasedRewardsLoadingError: WritableSignal<boolean> =
    signal(false);
  public isPurchasedRewardsLoadingSuccess: WritableSignal<boolean> =
    signal(false);
  public isPurchasedRewardsLoadingCompleted: WritableSignal<boolean> =
    signal(false);

  public points: WritableSignal<RewardPoints[]> = signal([]);
  public totalPoints = linkedSignal(() => {
    const points = this.points();
    if (!points.length) {
      return 0;
    }

    const sortedPoints = points.slice().sort((a, b) => {
      return b.rewardBankId - a.rewardBankId;
    });

    const selectedBalance = sortedPoints[0].balance;

    if (!selectedBalance.length) {
      return 0;
    }

    return selectedBalance[0].points;
  });
}

function countCompletedChallenges(challenges: AnyChallenge[]): number {
  return challenges.filter((item: AnyChallenge) => {
    if ([OfferType.SELECT_TO_WIN].includes(item.type)) return false;
    if (item.type === OfferType.STEP || item.type === OfferType.FREQUENCY)
      return item.isCompleted;
    return !!(item.reward > 0);
  }).length;
}
